import { useEffect, useCallback } from 'react';
import { handleError, ERROR_CATEGORY } from '../../../utils/ErrorUtils';

// Keyboard shortcut mappings (Roll20-style) - defined outside component for stability
// date_of_change: 2026-05-24 - Moved outside to fix react-hooks/exhaustive-deps
const SHORTCUTS = {
    // Navigation
    SPACE: ' ',           // Pan mode toggle
    PLUS: '+',           // Zoom in
    EQUALS: '=',         // Zoom in (alternative)
    MINUS: '-',          // Zoom out
    ZERO: '0',           // Reset zoom
    
    // Selection
    CTRL_A: 'ctrl+a',    // Select all
    DELETE: 'Delete',    // Delete selected
    BACKSPACE: 'Backspace', // Delete selected (alternative)
    
    // History
    CTRL_Z: 'ctrl+z',    // Undo
    CTRL_Y: 'ctrl+y',    // Redo
    CTRL_SHIFT_Z: 'ctrl+shift+z', // Redo (alternative)
    
    // Tools
    F: 'f',              // Freehand drawing
    L: 'l',              // Line tool
    R: 'r',              // Rectangle tool
    C: 'c',              // Circle tool
    T: 't',              // Text tool
    P: 'p',              // Polygon tool
    E: 'e',              // Eraser tool
    S: 's',              // Select tool
    
    // Clear
    CTRL_SHIFT_C: 'ctrl+shift+c', // Clear all drawings
    
    // Grid
    G: 'g',              // Toggle grid
    CTRL_G: 'ctrl+g',    // Toggle grid snap
};

/**
 * Custom hook for Roll20-style keyboard shortcuts
 * Provides keyboard navigation and tool switching functionality
 */
const useKeyboardShortcuts = ({
  onPanModeToggle = null,
  onToolChange = null,
  onZoomIn = null,
  onZoomOut = null,
  onResetZoom = null,
  onSelectAll = null,
  onDeleteSelected = null,
  onUndo = null,
  onRedo = null,
  onClearDrawings = null,
  enabled = true
}) => {
  // Check if key combination matches shortcut
  const matchesShortcut = useCallback((event, shortcut) => {
    try {
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey;
      const shift = event.shiftKey;
      const alt = event.altKey;

      switch (shortcut) {
        case SHORTCUTS.SPACE:
          return key === ' ' && !ctrl && !shift && !alt;
        case SHORTCUTS.PLUS:
        case SHORTCUTS.EQUALS:
          return (key === '+' || key === '=') && !ctrl && !shift && !alt;
        case SHORTCUTS.MINUS:
          return key === '-' && !ctrl && !shift && !alt;
        case SHORTCUTS.ZERO:
          return key === '0' && !ctrl && !shift && !alt;
        case SHORTCUTS.CTRL_A:
          return key === 'a' && ctrl && !shift && !alt;
        case SHORTCUTS.DELETE:
          return key === 'delete' && !ctrl && !shift && !alt;
        case SHORTCUTS.BACKSPACE:
          return key === 'backspace' && !ctrl && !shift && !alt;
        case SHORTCUTS.CTRL_Z:
          return key === 'z' && ctrl && !shift && !alt;
        case SHORTCUTS.CTRL_Y:
          return key === 'y' && ctrl && !shift && !alt;
        case SHORTCUTS.CTRL_SHIFT_Z:
          return key === 'z' && ctrl && shift && !alt;
        case SHORTCUTS.CTRL_SHIFT_C:
          return key === 'c' && ctrl && shift && !alt;
        case SHORTCUTS.CTRL_G:
          return key === 'g' && ctrl && !shift && !alt;
        default:
          // Single letter shortcuts
          return key === shortcut && !ctrl && !shift && !alt;
      }
    } catch (error) {
      handleError(
        error,
        false,
        {
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error matching keyboard shortcut'
        }
      );
      return false;
    }
  }, []); // SHORTCUTS is now a stable module-level constant

  // Handle keyboard events
  const handleKeyDown = useCallback((event) => {
    try {
      if (!enabled) return;

      // Prevent shortcuts when typing in input fields
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.contentEditable === 'true'
      )) {
        return;
      }

      let handled = false;

      // Navigation shortcuts
      if (matchesShortcut(event, SHORTCUTS.SPACE)) {
        if (onPanModeToggle) {
          onPanModeToggle();
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.PLUS) || matchesShortcut(event, SHORTCUTS.EQUALS)) {
        if (onZoomIn) {
          onZoomIn();
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.MINUS)) {
        if (onZoomOut) {
          onZoomOut();
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.ZERO)) {
        if (onResetZoom) {
          onResetZoom();
          handled = true;
        }
      }

      // Selection shortcuts
      else if (matchesShortcut(event, SHORTCUTS.CTRL_A)) {
        if (onSelectAll) {
          onSelectAll();
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.DELETE) || matchesShortcut(event, SHORTCUTS.BACKSPACE)) {
        if (onDeleteSelected) {
          onDeleteSelected();
          handled = true;
        }
      }

      // History shortcuts
      else if (matchesShortcut(event, SHORTCUTS.CTRL_Z)) {
        if (onUndo) {
          onUndo();
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.CTRL_Y) || matchesShortcut(event, SHORTCUTS.CTRL_SHIFT_Z)) {
        if (onRedo) {
          onRedo();
          handled = true;
        }
      }

      // Tool shortcuts
      else if (matchesShortcut(event, SHORTCUTS.F)) {
        if (onToolChange) {
          onToolChange('freehand');
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.L)) {
        if (onToolChange) {
          onToolChange('line');
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.R)) {
        if (onToolChange) {
          onToolChange('square');
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.C)) {
        if (onToolChange) {
          onToolChange('circle');
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.T)) {
        if (onToolChange) {
          onToolChange('text');
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.P)) {
        if (onToolChange) {
          onToolChange('polygon');
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.E)) {
        if (onToolChange) {
          onToolChange('eraser');
          handled = true;
        }
      } else if (matchesShortcut(event, SHORTCUTS.S)) {
        if (onToolChange) {
          onToolChange('select');
          handled = true;
        }
      }

      // Clear shortcuts
      else if (matchesShortcut(event, SHORTCUTS.CTRL_SHIFT_C)) {
        if (onClearDrawings) {
          onClearDrawings();
          handled = true;
        }
      }

      // Prevent default behavior for handled shortcuts
      if (handled) {
        event.preventDefault();
        event.stopPropagation();
      }
    } catch (error) {
      handleError(
        error,
        false,
        {
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error handling keyboard shortcut'
        }
      );
    }
  }, [
    enabled,
    matchesShortcut,
    onPanModeToggle,
    onToolChange,
    onZoomIn,
    onZoomOut,
    onResetZoom,
    onSelectAll,
    onDeleteSelected,
    onUndo,
    onRedo,
    onClearDrawings
    // SHORTCUTS is a stable module-level constant, not a dependency
  ]);

  // Set up keyboard event listeners
  useEffect(() => {
    if (!enabled) return;

    try {
      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    } catch (error) {
      handleError(
        error,
        false,
        {
          category: ERROR_CATEGORY.GENERAL,
          message: 'Error setting up keyboard shortcuts'
        }
      );
    }
  }, [enabled, handleKeyDown]);

  // Return shortcut information for UI display
  return {
    shortcuts: SHORTCUTS,
    enabled
  };
};

export default useKeyboardShortcuts;
